import { OfferedAnswer, OfferedAnswerApi, OptionalQuestion } from "../components/Form/types";
import {
  FormikCheckboxGroup,
  FormikFastField, FormikOfferedQuestionsRadioGroup,
  FormikRadioGroup,
} from "../components/Form/HospitalSurveyForm";
import { HospitalSurvey, SurveyAnswer } from "../types/hospital";
import { requiredQuestion } from "../constants/messages";
import { getOfferedAnswers } from "../api/hospital";

export const textQuestions = new Set([
  2, 3, 5, 6, 7, 8, 9, 11, 14, 15, 27, 29, 31, 32, 34, 35, 36, 37, 39, 41, 42, 48, 49, 50, 55, 56, 59, 60, 61, 62, 63, 68, 70, 72,
]);

export const yesNoQuestions = new Set([
  10, 12, 17, 18, 19, 20, 21, 22, 23, 25, 26, 28, 30, 33, 38, 40, 44, 45, 46, 47, 51, 52, 53, 54, 57, 58, 64, 65, 66, 67, 69, 71, 73, 74, 75, 76, 77
]);

export const offeredAnswersQuestions = new Set([1, 4, 16]);

export const radioOfferedAnswersQuestions = new Set([24, 43, 13]);

export const requiredQuestions = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77]);

export const optionalQuestions = new Set([52, 53, 54, 59]);

export const SERVICE_SELECTION_CATEGORY = 0;
export const BIRTH_CATEGORIES = [2, 3, 4, 5, 6, 7];
export const ABORTION_CATEGORY = 8;
export const MPO_CATEGORIES = [9, 10, 11, 12, 13];
export const ALL_SERVICE_CATEGORIES = BIRTH_CATEGORIES.concat(ABORTION_CATEGORY, MPO_CATEGORIES);
export const SERVICE_SELECTION_QUESTION = 1;
export const BIRTH_CHECKBOX = 0;
export const ABORTION_CHECKBOX = 1;
export const MPO_CHECKBOX = 2;


export function checkInputType(surveyAnswer: SurveyAnswer) {
  if (isRadioInput(surveyAnswer)) {
    return FormikRadioGroup;
  } else if (isCheckboxInput(surveyAnswer)) {
    return FormikCheckboxGroup;
  } else if (isTextInput(surveyAnswer)) {
    return FormikFastField;
  } else if (isOfferedAnswerRadioInput(surveyAnswer)) {
    return FormikOfferedQuestionsRadioGroup;
  }
}

export function isTextInput(surveyAnswer: SurveyAnswer) {
  return textQuestions.has(surveyAnswer.questionId);
}

export function isRadioInput(surveyAnswer: SurveyAnswer) {
  return yesNoQuestions.has(surveyAnswer.questionId);
}

export function isOfferedAnswerRadioInput(surveyAnswer: SurveyAnswer) {
  return radioOfferedAnswersQuestions.has(surveyAnswer.questionId);
}

export function isCheckboxInput(surveyAnswer: SurveyAnswer) {
  return offeredAnswersQuestions.has(surveyAnswer.questionId);
}

export function isRequired(surveyAnswer: SurveyAnswer) {
  return requiredQuestions.has(surveyAnswer.questionId);
}

export function isOptionalQuestion(questionId: number) {
  return optionalQuestions.has(questionId);
}

export function validateBlank(value: string) {
  let error;
  if (!value || !value.length) {
    error = requiredQuestion;
  }
  return error;
}

export async function createAnswersForCheckboxes(): Promise<
  Map<number, OfferedAnswer[]>
> {
  let offeredAnswers = new Map<number, OfferedAnswer[]>();
  for (const questionId of offeredAnswersQuestions) {
    offeredAnswers.set(questionId, []);
  }

  const result = await getOfferedAnswers();
  const offeredAnswersList: OfferedAnswerApi[] = result.data.offeredAnswerList;

  for (const offeredAnswer of offeredAnswersList) {
    if (radioOfferedAnswersQuestions.has(offeredAnswer.questionId)) {
      continue;
    }
    offeredAnswers.get(offeredAnswer.questionId)!.push({
      offeredAnswerText: offeredAnswer.text,
      selected: false,
    });
  }

  // Add empty offered answer to each question for "Nešto drugo"
  for (const key of offeredAnswers.keys()) {
    offeredAnswers.get(key)!.push({ offeredAnswerText: "", selected: false });
  }

  return offeredAnswers;
}

export async function createAnswersForRadios(): Promise<
  Map<number, OfferedAnswer[]>
> {
  let offeredAnswers = new Map<number, OfferedAnswer[]>();
  for (const questionId of radioOfferedAnswersQuestions) {
    offeredAnswers.set(questionId, []);
  }

  const result = await getOfferedAnswers();
  const offeredAnswersList: OfferedAnswerApi[] = result.data.offeredAnswerList;

  for (const offeredAnswer of offeredAnswersList) {
    if (offeredAnswersQuestions.has(offeredAnswer.questionId)) {
      continue;
    }
    offeredAnswers.get(offeredAnswer.questionId)!.push({
      offeredAnswerText: offeredAnswer.text,
      selected: false,
    });
  }

  return offeredAnswers;
}

export async function markCheckboxes(
  surveyData: HospitalSurvey
): Promise<Map<number, OfferedAnswer[]>> {
  const answersForCheckboxes: Map<number, OfferedAnswer[]> =
    await createAnswersForCheckboxes();

  for (const questionCategory of surveyData.answers) {
    for (const surveyAnswer of questionCategory.surveyAnswers) {
      if (isCheckboxInput(surveyAnswer) && surveyAnswer.answerText) {
        const tokenizedAnswer = surveyAnswer.answerText.split(", ");
        for (const [i, answer] of tokenizedAnswer.entries()) {
          for (let offeredAnswer of answersForCheckboxes.get(
            surveyAnswer.questionId
          )!) {
            if (
              answer.toLowerCase() ===
              offeredAnswer.offeredAnswerText.toLowerCase()
            ) {
              offeredAnswer.selected = true;
              tokenizedAnswer[i] = "";
            }
          }
        }
        let offeredAnswerTextForInputField: string = "";
        if (tokenizedAnswer.length) {
          for (const remainingText of tokenizedAnswer) {
            if (remainingText !== "") {
              offeredAnswerTextForInputField += remainingText;
            }
          }
        }
        offeredAnswerTextForInputField =
          offeredAnswerTextForInputField.charAt(0).toUpperCase() +
          offeredAnswerTextForInputField.slice(1);

        answersForCheckboxes.get(surveyAnswer.questionId)![
        answersForCheckboxes.get(surveyAnswer.questionId)!.length - 1
          ] = {
          offeredAnswerText: offeredAnswerTextForInputField,
          selected: offeredAnswerTextForInputField === "" ? false : true,
        };
      }
    }
  }
  return answersForCheckboxes;
}

export function surveyCategoryFilter(categoryIdx: number, hasBirth: boolean, hasAbortion: boolean, hasMpo: boolean,) {
  return ((hasBirth && BIRTH_CATEGORIES.includes(categoryIdx)) || (hasAbortion && categoryIdx === ABORTION_CATEGORY) || (hasMpo && MPO_CATEGORIES.includes(categoryIdx)) || !ALL_SERVICE_CATEGORIES.includes(categoryIdx))
}

export function surveyQuestionFilter(optionalQuestions: OptionalQuestion[], questionId: number, surveyAnswers: SurveyAnswer[]) {
  const optionalQuestion = optionalQuestions.find(value => value.optionalQuestionId === questionId)!;
  const question = surveyAnswers.find(value => value.questionId === optionalQuestion.questionId);

  return question?.answerText !== optionalQuestion.toggleAnswer;
}
